import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '../../api/client'
import { m365Connect, m365Disconnect, m365StatusQuery, type M365Status } from '../../api/settingsSecuritee'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings > Securitee > Microsoft 365: legatura firmei cu tenantul ei, ca
 * inventarul atasamentelor trimise sa se ia din Microsoft Graph (designul e in
 * `App\Securitee\M365\M365Connection`, in API).
 *
 * "Connect" nu deschide nimic singur in alta fereastra: duce pagina la ecranul
 * de acord Microsoft, iar Microsoft o aduce inapoi aici cu `?m365=...`. Adresa
 * de acord se poate si copia, pentru cand adminul Microsoft 365 al firmei e
 * altcineva decat omul din portal.
 */

const RESULT: Record<string, { cls: string; text: string }> = {
  connected: { cls: 'alert-success', text: 'Microsoft 365 is connected. Sent attachments will appear in Securitee after the next collection run.' },
  denied: { cls: 'alert-danger', text: 'The Microsoft 365 administrator did not grant consent.' },
  taken: { cls: 'alert-danger', text: 'This Microsoft 365 tenant is already connected to another company.' },
  invalid: { cls: 'alert-danger', text: 'The consent link was expired or already used. Press Connect again.' },
  not_configured: { cls: 'alert-warning', text: 'The Microsoft 365 integration is not configured on this server.' },
}

function resultFromUrl(): string {
  if (typeof window === 'undefined') return ''
  return new URLSearchParams(window.location.search).get('m365') ?? ''
}

function StateLabel({ status }: { status: M365Status }) {
  switch (status.state) {
    case 'connected':
      return status.lastError !== '' ? <span className="label label-warning">Connected, last run failed</span> : <span className="label label-success">Connected</span>
    case 'pending':
      return <span className="label label-info">Waiting for admin consent</span>
    case 'error':
      return <span className="label label-danger">Not connected (error)</span>
    default:
      return <span className="label label-default">Not connected</span>
  }
}

export function Microsoft365Panel() {
  const client = useQueryClient()
  const page = useQuery(m365StatusQuery())
  const [busy, setBusy] = useState(false)
  const [consentUrl, setConsentUrl] = useState('')
  const result = RESULT[resultFromUrl()]
  const reload = () => client.invalidateQueries({ queryKey: ['settings', 'securitee', 'm365'] })

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (!page.data) return <Spinner />
  const status = page.data

  const connect = async (go: boolean) => {
    if (busy) return
    setBusy(true)
    try {
      const { url } = await m365Connect()
      if (go) {
        window.location.assign(url)
      } else {
        setConsentUrl(url)
        void reload()
      }
    } catch {
      toast('Could not start the Microsoft 365 connection.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const disconnect = async () => {
    if (busy || !confirm('Disconnect Microsoft 365? Sent attachments will no longer be collected.')) return
    setBusy(true)
    try {
      await m365Disconnect()
      toast('Disconnected', 'Success', 'success')
      setConsentUrl('')
      void reload()
    } catch {
      toast('Could not disconnect.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div id="content" className="m365-panel">
      <header className="panel-heading">
        <h3>
          <strong>Microsoft 365</strong> sent attachments
        </h3>
        <label className="color">inventory of files sent by email, read from your Microsoft 365 tenant</label>
      </header>

      {result && <div className={'alert ' + result.cls + ' m365-result'}>{result.text}</div>}

      <p className="m365-intro">
        Securitee can list every attachment your users send by email - from Outlook, Outlook on the web or a phone - by reading the Sent Items folder of each mailbox
        through Microsoft Graph. Only the file name, size, type, recipients and date are read; the content of the messages and of the attachments is never downloaded.
        Nothing is installed or changed on the workstations.
      </p>
      <p className="m365-intro">
        Connecting requires a Microsoft 365 Global Administrator (or Privileged Role Administrator) of your organisation to approve the OptimumDesk application with the
        permissions <strong>Mail.Read</strong> and <strong>User.ReadBasic.All</strong>. Microsoft describes Mail.Read as access to all mailboxes; to limit it to some
        users, your administrator can scope the application to a mail-enabled group with Exchange Online RBAC for Applications.
      </p>

      {status.state === 'not_configured' ? (
        <div className="alert alert-warning m365-off">The Microsoft 365 integration is not configured on this server. Contact OptimumDesk support to enable it.</div>
      ) : status.state === 'not_migrated' ? (
        <div className="alert alert-warning m365-off">The Microsoft 365 tables are missing. The database migrations must be run first.</div>
      ) : (
        <table className="table m365-status">
          <tbody>
            <tr>
              <th>Status</th>
              <td>
                <StateLabel status={status} />
              </td>
            </tr>
            {status.tenantId && (
              <tr>
                <th>Tenant ID</th>
                <td className="m365-tenant">{status.tenantId}</td>
              </tr>
            )}
            {status.connectedAt && (
              <tr>
                <th>Connected on</th>
                <td>{status.connectedAt}</td>
              </tr>
            )}
            <tr>
              <th>Last collection</th>
              <td className="m365-last-run">{status.lastRunAt ?? 'Never'}</td>
            </tr>
            {status.state === 'connected' && (
              <tr>
                <th>Last run</th>
                <td>
                  {status.mailboxes} mailboxes read, {status.filesLastRun} attachments recorded
                </td>
              </tr>
            )}
            {status.lastError !== '' && (
              <tr>
                <th>Last error</th>
                <td className="m365-error text-danger">{status.lastError}</td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {status.canWrite && status.configured && status.ready && (
        <div className="m365-actions">
          {status.state === 'connected' ? (
            <button type="button" className="btn btn-danger btn-sm m365-disconnect" disabled={busy} onClick={() => void disconnect()}>
              Disconnect
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-primary btn-sm m365-connect" disabled={busy} onClick={() => void connect(true)}>
                <i className="fab fa-microsoft" aria-hidden="true"></i> Connect Microsoft 365
              </button>{' '}
              <button type="button" className="btn btn-default btn-sm m365-link" disabled={busy} onClick={() => void connect(false)}>
                Get a link for my administrator
              </button>
            </>
          )}
        </div>
      )}

      {consentUrl !== '' && (
        <div className="form-group m365-link-box">
          <label>Send this link to your Microsoft 365 administrator (valid 72 hours, single use):</label>
          <input type="text" readOnly className="form-control" value={consentUrl} onFocus={(e) => e.target.select()} />
        </div>
      )}

      {status.state === 'connected' && (
        <p className="m365-note text-muted">
          Disconnecting here stops the collection. To remove the permissions from your tenant, your administrator should also delete the OptimumDesk application under
          Microsoft Entra ID &gt; Enterprise applications.
        </p>
      )}
    </div>
  )
}
